import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Prompt} from 'react-router-dom';
import styled, {keyframes} from 'styled-components';

import navigationConfirmService from '../../shared/services/navigationConfirmService';

// Guard that covers two navigation scenarios when auto-save is OFF and the
// user has queued cell edits:
//
//  1. In-app navigation (React Router <Link> clicks, e.g. Timesheets ↔ Approvals)
//     Handled via <Prompt> + getUserConfirmation wired in routes.js.  Shows a
//     styled in-app dialog so the user can save first, stay, or leave anyway.
//
//  2. Browser close / refresh / address-bar navigation
//     Handled via the window beforeunload event (browsers show a generic
//     "Changes you made may not be saved" native prompt for this).
const UnsavedChangesGuard = ({pendingSaves, autoSave, flushPendingSaves}) => {
  const countRef = useRef(0);
  countRef.current = pendingSaves ? Object.keys(pendingSaves).length : 0;

  const [dialog, setDialog] = useState({open: false, callback: null});
  const [saving, setSaving] = useState(false);

  // Register this component as the confirm handler for in-app navigation.
  useEffect(() => {
    navigationConfirmService.register(callback => {
      setDialog({open: true, callback});
    });
    return () => navigationConfirmService.unregister();
  }, []);

  // beforeunload: browser close / hard refresh.
  useEffect(() => {
    const handler = e => {
      if (countRef.current > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved timesheet changes. Leave anyway?';
        return e.returnValue;
      }
      return undefined;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const resolve = allow => {
    if (dialog.callback) dialog.callback(allow);
    setDialog({open: false, callback: null});
  };

  const handleStay = () => resolve(false);
  const handleLeave = () => resolve(true);

  const handleSaveAndLeave = async () => {
    setSaving(true);
    try {
      await flushPendingSaves();
      resolve(true);
    } catch (e) {
      // Save failed — stay so the user can see the error and retry.
      resolve(false);
    } finally {
      setSaving(false);
    }
  };

  const count = countRef.current;

  // Only block navigation when auto-save is off and there are pending saves.
  const shouldPrompt = autoSave === false && count > 0;

  return (
    <>
      <Prompt when={shouldPrompt} message="__unsaved__" />

      {dialog.open && (
        <Overlay>
          <Modal>
            <IconRow>
              <SaveIcon className="fa fa-floppy-o" aria-hidden="true" />
            </IconRow>
            <Title>Don&rsquo;t forget to save!</Title>
            <Body>
              You have{' '}
              <strong>
                {count} unsaved change{count === 1 ? '' : 's'}
              </strong>{' '}
              on this timesheet. What would you like to do?
            </Body>
            <Buttons>
              <SaveButton
                type="button"
                onClick={handleSaveAndLeave}
                disabled={saving}>
                {saving ? (
                  <>
                    <i className="fa fa-spinner fa-spin" aria-hidden="true" />{' '}
                    Saving&hellip;
                  </>
                ) : (
                  <>
                    <i className="fa fa-check" aria-hidden="true" /> Save &amp;
                    continue
                  </>
                )}
              </SaveButton>
              <StayButton type="button" onClick={handleStay} disabled={saving}>
                <i className="fa fa-arrow-left" aria-hidden="true" /> Go back
              </StayButton>
              <LeaveButton
                type="button"
                onClick={handleLeave}
                disabled={saving}>
                Leave without saving
              </LeaveButton>
            </Buttons>
          </Modal>
        </Overlay>
      )}
    </>
  );
};

UnsavedChangesGuard.propTypes = {
  pendingSaves: PropTypes.object,
  autoSave: PropTypes.bool,
  flushPendingSaves: PropTypes.func
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(24px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.15s ease;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  padding: 32px 36px 28px;
  max-width: 420px;
  width: calc(100vw - 32px);
  text-align: center;
  animation: ${slideUp} 0.18s ease;
`;

const IconRow = styled.div`
  margin-bottom: 12px;
`;

const SaveIcon = styled.i`
  font-size: 2.4rem;
  color: #e6a817;
`;

const Title = styled.h4`
  margin: 0 0 10px;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
`;

const Body = styled.p`
  margin: 0 0 22px;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BaseButton = styled.button`
  width: 100%;
  padding: 9px 16px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.12s, border-color 0.12s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    margin-right: 5px;
  }
`;

const SaveButton = styled(BaseButton)`
  background: #337ab7;
  color: #fff;
  border-color: #2e6da4;

  &:hover:not(:disabled) {
    background: #286090;
    border-color: #204d74;
  }
`;

const StayButton = styled(BaseButton)`
  background: #fff;
  color: #333;
  border-color: #ccc;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }
`;

const LeaveButton = styled(BaseButton)`
  background: none;
  color: #999;
  border-color: transparent;
  font-size: 0.82rem;
  font-weight: 400;
  padding: 4px 8px;

  &:hover:not(:disabled) {
    color: #c0392b;
    text-decoration: underline;
  }
`;

export default UnsavedChangesGuard;
